import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CUSTOM_SIZE_MAX,
  CUSTOM_SIZE_MIN,
  clampCustomSize,
  getCustomSizeSuggestions,
} from "./customResizePreset";
import "./ResizeCustomSizeFlyout.css";

function LockToggleIcon({ locked }) {
  return (
    <svg
      className="rcs-flyout__lock-icon"
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="7"
        width="10"
        height="7"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      {locked ? (
        <path
          d="M5 7V5a3 3 0 016 0v2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      ) : (
        <path
          d="M5 7V5a3 3 0 015.8-1"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

function BackChevron() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
      <path
        d="M10 3L5 8l5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function digitsOnly(raw) {
  return String(raw || "").replace(/[^\d]/g, "").slice(0, 5);
}

/**
 * Custom Size view — rendered inside the left nav so it shares the same
 * bounds, overflow clip, and border-radius as the host panel.
 */
const ResizeCustomSizeFlyout = ({
  open = false,
  /** @deprecated kept for call-site compatibility */
  anchorEl = null,
  /** Left resize panel / prep column — overlay is portaled into this node. */
  panelRef: dockPanelRef = null,
  naturalWidth = 0,
  naturalHeight = 0,
  initialWidth = null,
  initialHeight = null,
  onContinue = () => {},
  onClose = () => {},
}) => {
  const [hostEl, setHostEl] = useState(null);
  const [widthDraft, setWidthDraft] = useState("");
  const [heightDraft, setHeightDraft] = useState("");
  const [lockAspect, setLockAspect] = useState(false);
  const [error, setError] = useState(null);
  const aspectRef = useRef(1);

  const suggestions = useMemo(
    () => getCustomSizeSuggestions(naturalWidth, naturalHeight),
    [naturalWidth, naturalHeight]
  );

  useEffect(() => {
    if (!open) {
      setHostEl(null);
      return undefined;
    }
    const el = dockPanelRef?.current || null;
    setHostEl(el);
    if (!el) return undefined;

    const prevPosition = el.style.position;
    const prevOverflow = el.style.overflow;
    const computed = window.getComputedStyle(el);
    if (computed.position === "static") {
      el.style.position = "relative";
    }
    el.style.overflow = "hidden";
    el.classList.add("rcs-custom-size-host");

    return () => {
      el.style.position = prevPosition;
      el.style.overflow = prevOverflow;
      el.classList.remove("rcs-custom-size-host");
    };
  }, [open, dockPanelRef]);

  useEffect(() => {
    if (!open) return;
    const iw =
      clampCustomSize(initialWidth) ??
      clampCustomSize(naturalWidth) ??
      1080;
    const ih =
      clampCustomSize(initialHeight) ??
      clampCustomSize(naturalHeight) ??
      1080;
    setWidthDraft(String(iw));
    setHeightDraft(String(ih));
    aspectRef.current = iw / ih;
    setLockAspect(false);
    setError(null);
  }, [open, initialWidth, initialHeight, naturalWidth, naturalHeight]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const applyWidthChange = (raw) => {
    const next = digitsOnly(raw);
    setWidthDraft(next);
    setError(null);
    if (!lockAspect || !next) return;
    const w = Number(next);
    if (!(w > 0) || !(aspectRef.current > 0)) return;
    const h = clampCustomSize(Math.round(w / aspectRef.current));
    if (h != null) setHeightDraft(String(h));
  };

  const applyHeightChange = (raw) => {
    const next = digitsOnly(raw);
    setHeightDraft(next);
    setError(null);
    if (!lockAspect || !next) return;
    const h = Number(next);
    if (!(h > 0) || !(aspectRef.current > 0)) return;
    const w = clampCustomSize(Math.round(h * aspectRef.current));
    if (w != null) setWidthDraft(String(w));
  };

  const toggleLock = () => {
    setLockAspect((prev) => {
      const next = !prev;
      if (next) {
        const w = Number(widthDraft);
        const h = Number(heightDraft);
        if (w > 0 && h > 0) aspectRef.current = w / h;
      }
      return next;
    });
  };

  const fillFromSuggestion = (row) => {
    const w = clampCustomSize(row.width);
    const h = clampCustomSize(row.height);
    if (w == null || h == null) return;
    setWidthDraft(String(w));
    setHeightDraft(String(h));
    aspectRef.current = w / h;
    setError(null);
  };

  const handleContinue = () => {
    const rawW = Math.round(Number(widthDraft));
    const rawH = Math.round(Number(heightDraft));
    const inRange = (n) =>
      Number.isFinite(n) && n >= CUSTOM_SIZE_MIN && n <= CUSTOM_SIZE_MAX;
    if (!inRange(rawW) || !inRange(rawH)) {
      setError(
        `Size must be between ${CUSTOM_SIZE_MIN} × ${CUSTOM_SIZE_MIN} and ${CUSTOM_SIZE_MAX.toLocaleString()} × ${CUSTOM_SIZE_MAX.toLocaleString()} px.`
      );
      return;
    }
    onContinue({ width: rawW, height: rawH });
  };

  void anchorEl;

  if (!open || !hostEl) return null;

  return createPortal(
    <div
      className="rcs-flyout rcs-flyout--panel"
      role="dialog"
      aria-modal="true"
      aria-label="Custom Size"
    >
      <div className="rcs-flyout__header">
        <button
          type="button"
          className="rcs-flyout__back"
          onClick={onClose}
          aria-label="Back"
        >
          <BackChevron />
        </button>
        <h2 className="rcs-flyout__title">Custom Size</h2>
      </div>

      <div className="rcs-flyout__body">
        <div className="rcs-flyout__fields">
          <label className="rcs-flyout__field">
            <span className="rcs-flyout__label">Width (px)</span>
            <input
              type="text"
              inputMode="numeric"
              className="rcs-flyout__input"
              value={widthDraft}
              onChange={(e) => applyWidthChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleContinue();
              }}
              aria-label="Custom width in pixels"
              autoFocus
            />
          </label>

          <button
            type="button"
            className={`rcs-flyout__lock${lockAspect ? " is-locked" : ""}`}
            onClick={toggleLock}
            aria-pressed={lockAspect}
            aria-label={lockAspect ? "Unlock aspect ratio" : "Lock aspect ratio"}
            title={lockAspect ? "Aspect ratio locked" : "Aspect ratio unlocked"}
          >
            <LockToggleIcon locked={lockAspect} />
          </button>

          <label className="rcs-flyout__field">
            <span className="rcs-flyout__label">Height (px)</span>
            <input
              type="text"
              inputMode="numeric"
              className="rcs-flyout__input"
              value={heightDraft}
              onChange={(e) => applyHeightChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleContinue();
              }}
              aria-label="Custom height in pixels"
            />
          </label>
        </div>

        {error ? <p className="rcs-flyout__error">{error}</p> : null}

        <button
          type="button"
          className="rcs-flyout__continue"
          onClick={handleContinue}
        >
          Continue
        </button>

        {suggestions.length > 0 ? (
          <ul className="rcs-flyout__suggestions" role="list">
            {suggestions.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className="rcs-flyout__suggestion"
                  onClick={() => fillFromSuggestion(row)}
                >
                  <span className="rcs-flyout__suggestion-label">{row.label}</span>
                  <span className="rcs-flyout__suggestion-meta">{row.meta}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>,
    hostEl
  );
};

export default ResizeCustomSizeFlyout;
